



















































































import { Component, Prop, Vue } from 'vue-property-decorator';
import DateSelection from '@/components/DateSelection.vue';
import { routeMod } from '@/store/modules/route';
import { searchMod } from '@/store/modules/search';
import { Route, RouteStop, SearchResponse, SearchResultType, TrackableAssetStateChangedMessage } from '@/api/types';
import SearchResultsList from '@/components/SearchResultsList.vue';
import RouteStopsList from '@/components/RouteStopsList.vue';
import { TrackableAssetState } from '@/api/types/tracking';
import { toLocalDateFormatted, setFilterOptionDateByName, toDateIsoString } from '@/common/utils/date';
import { bus } from '@/services/busService';
import { trackingMod } from '@/store/modules/tracking';
import { SearchFilters } from '@/types/search';
import { decodePath } from '@/common/utils/polyline';
import { calculateBounds } from '@/common/utils/geometry';
import { isCompletedOrClosed } from '@/common/utils/routes';
import Tracking from '@/api/tracking';

@Component({
  components: {
    DateSelection,
    SearchResultsList,
    RouteStopsList
  }
})
export default class SideBarSearch extends Vue {
  @Prop({ required: true })
  showFullSearch!: Boolean;

  private loadingSearchResults = false;
  private loadingRouteData = false;
  private searchFilters: SearchFilters = {
    searchText: null,
    dateFrom: toDateIsoString(new Date()),
    dateTo: toDateIsoString(new Date()),
    dateFilterOption: 'Today'
  };

  get selectedItem(): SearchResponse | null {
    return searchMod.getSelectedItem;
  }

  get selectedRouteData(): Route | null {
    return routeMod.getSelectedRouteTrackingData;
  }

  get searchResults(): SearchResponse[] {
    return searchMod.getSearchResults;
  }

  get routes(): Route[] {
    return routeMod.getRoutes;
  }

  get visibleItems(): SearchResponse[] {
    return searchMod.getVisibleItems;
  }

  get completedRoutes(): string[] {
    const routes = this.routes
      .filter((route) => isCompletedOrClosed(route.routeStatus))
      .map((route) => route.deliveryRunReference);
    return routes;
  }

  get hasRouteData(): boolean {
    return !!this.selectedRouteData && this.selectedItem?.searchResultType === SearchResultType.route;
  }

  get regNumber(): string {
    if (this.hasRouteData) {
      return this.selectedRouteData?.trackable?.registrationNumber || '';
    } else {
      return this.selectedItem?.registrationNumber || '';
    }
  }

  get selectedItemColour(): any {
    return {
      'background-color': searchMod.getSelectedItemColour
    };
  }

  get lastTravelledPath(): TrackableAssetState | null {
    const travelledPath = this.selectedRouteData?.travelledPath;
    return travelledPath ? travelledPath[travelledPath.length - 1] : null;
  }

  get lastSeenDateTime(): string {
    if (this.selectedItem?.searchResultType === SearchResultType.route) {
      return this.lastTravelledPath
        ? `${toLocalDateFormatted(this.lastTravelledPath.locationDateTimeUtc, 'DD/MM/YY HH:mm:ss')}`
        : 'NOT AVAILABLE';
    } else {
      const trackableDateTime = this.getTrackableDateTimeUtc(this.selectedItem?.trackingDeviceIdentifier);

      return trackableDateTime ? `${toLocalDateFormatted(trackableDateTime, 'DD/MM/YY HH:mm:ss')}` : 'NOT AVAILABLE';
    }
  }

  get routeStatusClassName(): string {
    return this.selectedItem && this.selectedItem.deliveryRunReference
      ? this.selectedItem.routeStatus.toLowerCase()
      : '';
  }

  private handleFilterOptionChange(startDate: string, endDate: string, name: string): void {
    this.searchFilters.dateFrom = startDate;
    this.searchFilters.dateTo = endDate;
    this.searchFilters.dateFilterOption = name;
  }

  private clearSearchResultsAndRoutes(): void {
    searchMod.RESET_SEARCH_RESULTS();
    routeMod.RESET_ROUTES();
  }

  private resetSearch(): void {
    bus.$emit('closeInfoWindow', null);
    this.clearSearchResultsAndRoutes();
    this.searchFilters = {
      searchText: null,
      dateFrom: toDateIsoString(new Date()),
      dateTo: toDateIsoString(new Date()),
      dateFilterOption: 'Today'
    };
    this.$emit('updatePanelSize', false);
    bus.$emit('resetDate');
  }

  private getTrackableDateTimeUtc(trackingDeviceId: string | undefined): Date | null {
    return trackingDeviceId ? trackingMod.getTrackableDateTimeUtc(trackingDeviceId) : null;
  }

  private async searchItems(): Promise<void> {
    try {
      this.loadingSearchResults = true;
      bus.$emit('closeInfoWindow', null);
      this.clearSearchResultsAndRoutes();
      this.updateNewDateRange();
      if (this.searchFilters.searchText) {
        this.searchFilters.searchText = this.searchFilters.searchText.trim();
      }
      await searchMod.fetchSearchResults(this.searchFilters);
      this.loadingSearchResults = false;
      this.$emit('updatePanelSize', true);

      const trackableAssets = await Tracking.GetTrackableAssetStates();

      trackableAssets.forEach((trackableAsset) => {
        let trackableAssetStateChangedMessage = {
          trackingDeviceType: trackableAsset.trackingDeviceType,
          trackingDeviceIdentifier: trackableAsset.trackingDeviceIdentifier,
          trackableAssetStates: trackableAsset.trackableAssetStates
        } as TrackableAssetStateChangedMessage;

        trackingMod.setTrackableAssetState(trackableAssetStateChangedMessage);
      });
    } catch (err: unknown) {
      console.error(err);
      this.loadingSearchResults = false;
    }
  }

  private async fetchRoutes(): Promise<void> {
    try {
      await routeMod.fetchRoutes(this.searchFilters);
      this.fitBounds();
    } catch (err) {
      console.error(err);
    }
  }

  private async fetchRoute(item: SearchResponse, colour: string): Promise<void> {
    try {
      this.loadingRouteData = true;
      if (!this.routes.find((x: Route) => x.deliveryRunReference === item.deliveryRunReference)) {
        await routeMod.fetchRoute(item.deliveryRunReference);
      }
      const routeStatus = this.routes.find(
        (x: Route) => x.deliveryRunReference === item.deliveryRunReference
      )?.routeStatus;
      const updatedColour = isCompletedOrClosed(routeStatus) ? '#757576' : colour;
      searchMod.SET_SELECTED_ITEM(item);
      searchMod.SET_SELECTED_ITEM_COLOUR(updatedColour);
      routeMod.setSelectedRouteTrackingData(item.deliveryRunReference);
      this.loadingRouteData = false;
      const routeData = this.routes.find((x) => x.deliveryRunReference === item.deliveryRunReference);
      if (routeData) {
        this.fitRouteBounds(routeData);
      }
    } catch (err) {
      console.error(err);
      this.loadingRouteData = false;
    }
  }

  private panTo(trackingDeviceIdentifier: string): void {
    const trackableAssetLocation = trackingMod.getTrackableAssetLocation(trackingDeviceIdentifier);
    if (trackableAssetLocation) {
      bus.$emit('panTo', trackableAssetLocation);
    }
  }

  private panToSelectedVehicle(): void {
    let trackableAssetLocation = null;
    if (this.hasRouteData) {
      const trackingDeviceId = this.selectedRouteData?.trackable?.trackingDeviceIdentifier;
      if (trackingDeviceId) {
        trackableAssetLocation = trackingMod.getTrackableAssetLocation(trackingDeviceId);
      }
    } else {
      if (this.selectedItem) {
        trackableAssetLocation = trackingMod.getTrackableAssetLocation(this.selectedItem.trackingDeviceIdentifier);
      }
    }

    if (!!trackableAssetLocation && !isCompletedOrClosed(this.selectedRouteData?.routeStatus)) {
      bus.$emit('panTo', trackableAssetLocation);
      bus.$emit('openVehicleMarkerInfo', this.selectedItem, trackableAssetLocation);
    }
  }

  private fitBounds(): void {
    let latitudes: number[] = [];
    let longitudes: number[] = [];

    this.routes.forEach((route) => {
      route.routeStops.forEach((stop) => {
        latitudes.push(stop.location.latitude);
        longitudes.push(stop.location.longitude);
      });

      route.travelledPath.forEach((path) => {
        latitudes.push(path.latitude);
        longitudes.push(path.longitude);
      });

      if (route.directions) {
        route.directions.legs.forEach((leg) => {
          decodePath(leg.encodedPolyLine).forEach((location) => {
            latitudes.push(location.lat);
            longitudes.push(location.lng);
          });
        });
      }
    });

    const trackables = this.searchResults.filter((item) => item.searchResultType === SearchResultType.trackable);
    trackables.forEach((item) => {
      const trackableAssetLocation = trackingMod.getTrackableAssetLocation(item.trackingDeviceIdentifier);
      if (trackableAssetLocation) {
        latitudes.push(trackableAssetLocation?.latitude);
        longitudes.push(trackableAssetLocation?.longitude);
      }
    });

    if (latitudes.length > 0 && longitudes.length > 0) {
      bus.$emit('fitBounds', calculateBounds(latitudes, longitudes));
    }
  }

  private fitRouteBounds(route: Route): void {
    let latitudes: number[] = [];
    let longitudes: number[] = [];

    route.routeStops.forEach((stop: RouteStop) => {
      latitudes.push(stop.location.latitude);
      longitudes.push(stop.location.longitude);
    });

    if (latitudes.length > 0 && longitudes.length > 0) {
      bus.$emit('fitBounds', calculateBounds(latitudes, longitudes));
    }
  }

  private updateNewDateRange(): void {
    if (!this.searchFilters.dateFilterOption.includes('-')) {
      const dateOptions = setFilterOptionDateByName(this.searchFilters.dateFilterOption);
      this.searchFilters.dateFrom = dateOptions.startDateTimeUtc;
      this.searchFilters.dateTo = dateOptions.endDateTimeUtc;
    }
  }
}
